// researchSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IResearch, IResearchInitialState } from "./researchModal";
import {
  activeInactiveResearch,
  createResearch,
  deleteResearch,
  getAllActiveResearches,
  getAllResearches,
  getResearchByID,
  getResearchByURL,
  researchImageUpload,
  updateResearch,
} from "./services/research.services";

const initialState: IResearchInitialState = {
  loading: false,
  formLoading: false,
  error: "",
  ResearchID: null,
  ResearchList: [],
  ResearchDetail: {
    ID: null,
    Title: "",
    URL: "",
    Tagline: "",
    Date: "",
    PropertyTypeID: null,
    ShortDescription: "",
    LongDescription: "",
    SequenceNo: null,
    IsDisplayHome: false,
    MetaTitle: "",
    MetaKeywords: "",
    MetaDescription: "",
    MainImage: "",
    BannerImage: "",
    IsActive: 1,
  },
  FilterResearch: {
    Title: "",
    PropertyTypeName: "",
  },
};

const researchSlice = createSlice({
  name: "research",
  initialState,
  reducers: {
    clearResearchDetails: (state) => {
      state.ResearchDetail = initialState.ResearchDetail;
    },
    setResearchID: (state, action) => {
      state.ResearchID = action.payload;
    },
    clearResearchID: (state) => {
      state.ResearchID = initialState.ResearchID;
    },
    setFilterResearch: (state, action) => {
      state.FilterResearch = action.payload;
    },
    clearFilterResearch: (state) => {
      state.FilterResearch = initialState.FilterResearch;
    },
  },
  extraReducers: (builder) => {
    // GET ALL
    builder.addCase(getAllResearches.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllResearches.fulfilled,
      (state, action: PayloadAction<IResearch[]>) => {
        state.loading = false;
        state.ResearchList = action.payload || [];
      },
    );
    builder.addCase(getAllResearches.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.ResearchList = [];
    });

    // CREATE
    builder.addCase(createResearch.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(createResearch.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(createResearch.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // UPDATE
    builder.addCase(updateResearch.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(updateResearch.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(updateResearch.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // DELETE
    builder.addCase(deleteResearch.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(deleteResearch.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteResearch.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // GET BY ID
    builder.addCase(getResearchByID.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(
      getResearchByID.fulfilled,
      (state, action: PayloadAction<IResearch>) => {
        state.formLoading = false;
        state.ResearchDetail = action.payload;
      },
    );
    builder.addCase(getResearchByID.rejected, (state, action) => {
      state.formLoading = false;
      state.ResearchDetail = initialState.ResearchDetail;
      state.error = action.error.message || "Something went wrong";
    });

    // GET BY URL
    builder.addCase(getResearchByURL.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(
      getResearchByURL.fulfilled,
      (state, action: PayloadAction<IResearch>) => {
        state.formLoading = false;
        state.ResearchDetail = action.payload;
      },
    );
    builder.addCase(getResearchByURL.rejected, (state, action) => {
      state.formLoading = false;
      state.ResearchDetail = initialState.ResearchDetail;
      state.error = action.error.message || "Something went wrong";
    });

    // GET ALL ACTIVE
    builder.addCase(getAllActiveResearches.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllActiveResearches.fulfilled,
      (state, action: PayloadAction<IResearch[]>) => {
        state.loading = false;
        state.ResearchList = action.payload || [];
      },
    );
    builder.addCase(getAllActiveResearches.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.ResearchList = [];
    });

    // ACTIVE / INACTIVE
    builder.addCase(activeInactiveResearch.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(activeInactiveResearch.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(activeInactiveResearch.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // IMAGE UPLOAD
    builder.addCase(researchImageUpload.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(researchImageUpload.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(researchImageUpload.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const {
  clearResearchDetails,
  setResearchID,
  clearResearchID,
  setFilterResearch,
  clearFilterResearch,
} = researchSlice.actions;

export default researchSlice.reducer;
